import { Colors, Text } from '@amelio/ui-library';

import type { DraggableContainer, DraggableContainerMetadata } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

const ITEM_SLOT_HEIGHT = 57;
const CONTAINER_VERTICAL_PADDING = 32;
const MIN_RANKING_CONTAINER_HEIGHT = 200;

interface RankingContainerProps {
    container: DraggableContainer<{ label: string }>;
    metadata: DraggableContainerMetadata;
    children: React.ReactNode;
    hasError: boolean;
    isNextContainer: boolean;
    isRankingContainer: boolean;
    allItemsRanked?: boolean;
    totalOptions?: number;
}

export const RankingContainer = ({
    container,
    metadata,
    children,
    hasError,
    isNextContainer,
    isRankingContainer,
    allItemsRanked = false,
    totalOptions = 0,
}: RankingContainerProps) => {
    const { t } = useAmelioTranslations();

    const hasItemsToRank = container.items.length > 0;

    const nextElementLabel = t('evaluation360Questionnaire.rankingQuestion.nextElementToRank');
    const dragDropTipLabel = t('evaluation360Questionnaire.rankingQuestion.dragDropTip');
    const yourRankingLabel = t('evaluation360Questionnaire.rankingQuestion.yourRanking');

    if (isNextContainer) {
        if (!hasItemsToRank && allItemsRanked) {
            return null;
        }

        return (
            <div className={'space-y-2'}>
                <div className={'flex items-center justify-between'}>
                    <Text
                        weight={'semibold'}
                        size={'large'}
                        color={Colors.WHITE}
                    >
                        {nextElementLabel}
                    </Text>

                    <Text
                        size={'small'}
                        color={Colors.WHITE}
                        className={'opacity-60'}
                    >
                        {dragDropTipLabel}
                    </Text>
                </div>

                <div className={`min-h-[68px] ${hasError ? 'animate-shake' : ''}`}>{children}</div>
            </div>
        );
    }

    if (isRankingContainer) {
        return (
            <div className={'space-y-3'}>
                <Text
                    weight={'semibold'}
                    size={'large'}
                    color={Colors.WHITE}
                >
                    {yourRankingLabel}
                </Text>

                <div
                    className={`space-y-3 rounded-lg p-4 transition-colors ${
                        metadata.isOver ? 'bg-white/10' : ''
                    }`}
                    style={{
                        minHeight: Math.max(
                            MIN_RANKING_CONTAINER_HEIGHT,
                            (totalOptions - 1) * ITEM_SLOT_HEIGHT + CONTAINER_VERTICAL_PADDING,
                        ),
                    }}
                >
                    {children}
                </div>
            </div>
        );
    }

    return <>{children}</>;
};
